import React, { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import './Overview.css';

interface OverviewProps {
  tenantId: string;
  onBack: () => void;
}

interface Tenant {
  id: string;
  trial: boolean;
  consented: boolean;
  tenantCreated?: string;
  trialExpiration?: string;
}

interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  assignedSignatures: string[];
}

interface Signature {
  id: string;
  name: string;
}

const Overview: React.FC<OverviewProps> = ({ tenantId, onBack }) => {
  const [loading, setLoading] = useState(true);
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [signatures, setSignatures] = useState<Signature[]>([]);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<any>(null);
  const { instance, accounts } = useMsal();

  const getAccessToken = async () => {
    const account = accounts[0];
    if (!account) return null;
    try {
      const response = await instance.acquireTokenSilent({
        scopes: ['User.Read'],
        account: account
      });
      return response.accessToken;
    } catch (error) {
      console.error('Failed to acquire token:', error);
      return null;
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  const loadData = async () => {
    try {
      const token = await getAccessToken();
      if (!token) return;

      setLoading(true);

      // Fetch tenant info, users, and signatures in parallel
      const [tenantRes, usersRes, signaturesRes] = await Promise.all([
        fetch(`/api/tenant/${tenantId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`/api/users/${tenantId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`/api/signatures/${tenantId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (tenantRes.ok) {
        const data = await tenantRes.json();
        setTenant(data.tenant);
      }

      if (usersRes.ok) {
        const data = await usersRes.json();
        setUsers(data.users || []);
      }

      if (signaturesRes.ok) {
        const data = await signaturesRes.json();
        setSignatures(data.signatures || []);
      }
    } catch (error) {
      console.error('Failed to load overview data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    try {
      const token = await getAccessToken();
      if (!token) return;

      setSyncing(true);
      const response = await fetch('/api/tenant/sync', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ tenantId })
      });

      if (response.ok) {
        const data = await response.json();
        setSyncResult(data);
        // Reload data to reflect changes
        await loadData();
      } else {
        setSyncResult({ error: 'Failed to sync tenant. Please try again.' });
      }
    } catch (error) {
      console.error('Failed to sync tenant:', error);
      setSyncResult({ error: 'An error occurred while syncing tenant.' });
    } finally {
      setSyncing(false);
    }
  };

  const calculateMetrics = () => {
    const totalUsers = users.length;
    const usersWithSignatures = users.filter(u => u.assignedSignatures?.length > 0).length;
    const usersWithoutSignatures = totalUsers - usersWithSignatures;
    const coveragePercentage = totalUsers > 0 ? Math.round((usersWithSignatures / totalUsers) * 100) : 0;
    const totalSignatures = signatures.length;

    return {
      totalUsers,
      usersWithSignatures,
      usersWithoutSignatures,
      coveragePercentage,
      totalSignatures
    };
  };

  const getAccountStatus = () => {
    if (!tenant) return { type: 'Unknown', className: 'unknown', daysRemaining: null };

    if (tenant.trial) {
      if (tenant.trialExpiration) {
        const expirationDate = new Date(tenant.trialExpiration);
        const today = new Date();
        const daysRemaining = Math.ceil((expirationDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
        
        if (daysRemaining <= 0) {
          return { type: 'Trial Expired', className: 'expired', daysRemaining: 0 };
        }
        return { type: 'Trial', className: 'trial', daysRemaining };
      }
      return { type: 'Trial', className: 'trial', daysRemaining: null };
    }
    
    return { type: 'Active Subscription', className: 'active', daysRemaining: null };
  };

  const metrics = calculateMetrics();
  const accountStatus = getAccountStatus();

  return (
    <div className="overview-container">
      <div className="overview-header">
        <button className="btn-back" onClick={onBack}>← Back</button>
        <h1>Overview</h1>
        <button 
          className="btn-sync-overview"
          onClick={handleSync}
          disabled={syncing}
        >
          {syncing ? '🔄 Syncing...' : 'Sync Tenant'}
        </button>
      </div>

      {loading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading metrics...</p>
        </div>
      ) : (
        <div className="overview-content">
          {/* Account Status Card */}
          <div className="metric-card account-status-card">
            <h2>Account Status</h2>
            <div className="account-status-content">
              <span className={`status-badge ${accountStatus.className}`}>
                {accountStatus.type}
              </span>
              {accountStatus.daysRemaining !== null && (
                <p className="days-remaining">
                  {accountStatus.daysRemaining > 0 
                    ? `${accountStatus.daysRemaining} day${accountStatus.daysRemaining !== 1 ? 's' : ''} remaining`
                    : 'Expired'
                  }
                </p>
              )}
            </div>
          </div>

          {/* User Metrics Grid */}
          <div className="metrics-grid">
            <div className="metric-card">
              <div className="metric-icon"></div>
              <div className="metric-value">{metrics.totalUsers}</div>
              <div className="metric-label">Total Users</div>
            </div>

            <div className="metric-card">
              <div className="metric-icon"></div>
              <div className="metric-value">{metrics.usersWithSignatures}</div>
              <div className="metric-label">Users with Signatures</div>
            </div>

            <div className="metric-card">
              <div className="metric-icon"></div>
              <div className="metric-value">{metrics.usersWithoutSignatures}</div>
              <div className="metric-label">Users without Signatures</div>
            </div>

            <div className="metric-card">
              <div className="metric-icon"></div>
              <div className="metric-value">{metrics.totalSignatures}</div>
              <div className="metric-label">Total Signatures</div>
            </div>
          </div>

          {/* Coverage Card */}
          <div className="metric-card coverage-card">
            <h2>Signature Coverage</h2>
            <div className="coverage-content">
              <div className="coverage-percentage">{metrics.coveragePercentage}%</div>
              <div className="coverage-bar">
                <div 
                  className="coverage-fill" 
                  style={{ width: `${metrics.coveragePercentage}%` }}
                ></div>
              </div>
              <p className="coverage-text">
                {metrics.coveragePercentage === 100 
                  ? 'All users have signatures assigned! 🎉'
                  : `${metrics.usersWithoutSignatures} user${metrics.usersWithoutSignatures !== 1 ? 's' : ''} still need${metrics.usersWithoutSignatures === 1 ? 's' : ''} signature assignment`
                }
              </p>
            </div>
          </div>
        </div>
      )}

      {(syncing || syncResult) && (
        <div className="sync-modal-overlay" onClick={() => !syncing && setSyncResult(null)}>
          <div className="sync-modal" onClick={(e) => e.stopPropagation()}>
            {syncing ? (
              <div className="sync-in-progress">
                <div className="sync-spinner"></div>
                <h3>Syncing Tenant</h3>
                <p>Syncing users and signature assignments from Microsoft 365...</p>
              </div>
            ) : syncResult?.error ? (
              <div className="sync-error">
                <div className="error-icon">⚠️</div>
                <h3>Sync Failed</h3>
                <p>{syncResult.error}</p>
                <button className="btn-close-modal" onClick={() => setSyncResult(null)}>
                  Close
                </button>
              </div>
            ) : (
              <div className="sync-success">
                <div className="success-icon">✓</div>
                <h3>Tenant sync complete!</h3>
                <button className="btn-close-modal" onClick={() => setSyncResult(null)}>
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Overview;
